import React, { useEffect, useState } from "react";

/**
 * Käyttöohje sovelluksen sisällä, roolin mukaan (pelaaja / järjestäjä /
 * ylläpitäjä). Aukeaa käyttäjän oman roolin kohdalta, mutta muutkin
 * välilehdet näkyvät: ylläpitäjä näkee mitä pelaajat näkevät.
 *
 * Teksti on tässä eikä erillisessä dokumentissa, jotta se päivittyy
 * samassa muutoksessa kuin ominaisuus. Jos muutat napin nimeä tai
 * toimintoa, päivitä sama kohta tähän.
 */

type GuideRole = "player" | "organizer" | "admin";

/**
 * Säännöt eivät ole rooli: ne koskevat kaikkia samalla tavalla. Siksi
 * välilehti on oma tyyppinsä eikä `GuideRole`en lisätty arvo — muuten
 * `guideRoleFor` joutuisi palauttamaan jotain mitä mikään kirjautuminen
 * ei koskaan tuota.
 */
type GuideTab = GuideRole | "rules";
type Section = { title: string; items: string[] };

const GUIDE: Record<"fi" | "en", Record<GuideRole, Section[]>> = {
  fi: {
    player: [
      {
        title: "Kirjautuminen",
        items: [
          "Helpoin tapa: avaa sähköpostin kutsu ja paina Vastaa kutsuun. Pääset sisään ilman klubikoodia ja PIN-koodia.",
          "Toinen tapa: syötä klubikoodi, sitten tapahtuman PIN-koodi ja nimesi. Koodit saat järjestäjältä.",
          "Ensimmäisellä kerralla puhelin tallentaa pääsyavaimen (passkey). Jatkossa kirjaudut Face ID:llä, sormenjäljellä tai puhelimen omalla koodilla.",
          "Pysyt kirjautuneena, kunnes painat Kirjaudu ulos.",
        ],
      },
      {
        title: "Tapahtumat ja ilmoittautuminen",
        items: [
          "Tapahtumat-välilehdellä ovat klubin tulevat tapahtumat.",
          "📩 KUTSU tarkoittaa, että sinut on kutsuttu. Vastaa Tulen tai En pääse.",
          "Muihin tapahtumiin ilmoittaudut napilla Ilmoittaudu. Peruminen: napauta Olet mukana uudelleen.",
          "Voit muuttaa vastaustasi milloin tahansa ennen tapahtumaa.",
          "Jos et ole vastannut kutsuun, saat muistutuksen noin puolitoista vuorokautta ennen tapahtumaa.",
        ],
      },
      {
        title: "Pelipäivänä",
        items: [
          "Avaa tapahtuma: näet kentät, kierrokset ja Omat ottelusi.",
          "Tulokset kirjaa järjestäjä tai ylläpitäjä. Saat ilmoituksen, kun ottelusi tulos on kirjattu.",
          "Tapahtuman Viestit-osiossa voit kertoa muille esimerkiksi myöhästymisestä tai vapaasta paikasta. Pikanapit hoitavat yleisimmät viestit.",
          "Jos kirjoittaminen vaatii vahvistuksen, paina Lähetä minulle vahvistuslinkki. Linkki tulee profiiliisi tallennettuun sähköpostiin; avaa se puhelimella, jolla käytät Spinloopia.",
          "Asiattoman viestin voi ilmoittaa ylläpitäjälle tai estää kirjoittajan viestit omasta näkymästä. Viestit poistuvat viikon kuluttua tapahtumasta.",
          "Jos klubi käyttää MobilePay-lipasta, tapahtumassa on Maksa MobilePay -nappi.",
        ],
      },
      {
        title: "Liiga",
        items: [
          "Liiga-välilehti näkyy, kun klubilla on kausi käynnissä.",
          "Illan pisteet = sijoituspisteet + läsnäolopiste. Läsnäolopisteen saa, kun on paikalla, vaikka ei ehtisi pelata.",
          "Tasapisteissä pelaajat jakavat saman sijan ja samat pisteet.",
        ],
      },
      {
        title: "Profiili ja DUPR",
        items: [
          "Profiilissa voit yhdistää DUPR-tilisi (Yhdistä DUPR-tili). Silloin klubin ottelut päivittävät DUPR-ratingiasi.",
          "Ota ilmoitukset käyttöön profiilissa, niin saat kutsut, muistutukset ja tulokset puhelimeen.",
          "Historia-välilehdellä ovat aiemmat ottelusi.",
        ],
      },
    ],
    organizer: [
      {
        title: "Järjestäjäksi",
        items: [
          "Ylläpitäjä nimeää sinut järjestäjäksi ja lähettää kutsulinkin sähköpostiisi.",
          "Avaa linkki kerran. Puhelin tallentaa pääsyavaimen, ja sen jälkeen näet Järjestä-välilehden.",
          "Tapahtuman PIN-koodilla kirjautuessa saat aina vain pelaajan oikeudet, koska PIN on koko porukan yhteinen.",
        ],
      },
      {
        title: "Ennen iltaa",
        items: [
          "Avaa tapahtuma Järjestä-välilehdeltä.",
          "Lisää pelaajia klubin listasta tai + Lisää vieras.",
          "Lähetä kutsut: jokainen saa sähköpostin ja ilmoituksen puhelimeen. Sähköpostin linkillä pääsee sisään ilman PIN-koodia.",
          "Osallistujalistasta näet kuka on vahvistanut, kuka on kutsuttu mutta ei vastannut ja kuka ei pääse.",
        ],
      },
      {
        title: "Illan aikana",
        items: [
          "Merkitse Paikalla? jokaiselle, joka tuli. Pelanneet merkitään paikalle automaattisesti illan päättyessä.",
          "Arvo ottelut napilla Americano, tai muodosta kiinteät parit round robinia varten.",
          "Americano jakaa myös kentän päät tasaisesti. Jos päät eivät ole samanarvoisia (seinä lähellä toista takakenttää), nimeä ne Asetuksissa pelipaikan kohdalta — silloin päät näkyvät otteluissa.",
          "Lisää kierros arpoo seuraavan kierroksen. Lisää ottelu lisää yksittäisen ottelun käsin.",
          "Kirjaa tulokset otteluihin. 0–0 tarkoittaa pelaamatonta ottelua; tasapeli on sallittu tulos.",
          "Kerro muutoksista tapahtuman Viestit-osiossa (esim. kenttä vaihtui). Järjestäjän viesti menee aina perille, ja voit poistaa asiattoman viestin.",
        ],
      },
      {
        title: "Illan päätteeksi",
        items: [
          "Paina Päätä tapahtuma. Sovellus varoittaa, jos joltain ottelulta puuttuu tulos tai jos joku ilmoittautunut ei pelannut eikä ole merkitty paikalle.",
          "Päättäminen kirjaa sijoitukset, liigapisteet ja pelaajien tilastot.",
          "Korjaus jälkikäteen: avaa päättynyt tapahtuma, korjaa tulos ja paina Laske sijoitukset uudelleen.",
        ],
      },
      {
        title: "Mitä järjestäjä ei tee",
        items: [
          "Tapahtumien luonti, muokkaus ja poisto, DUPR-lähetys, kaudet ja klubin asetukset ovat ylläpitäjän.",
        ],
      },
    ],
    admin: [
      {
        title: "Kirjautuminen",
        items: [
          "Kirjaudu klubikoodilla ja ylläpitäjän PIN-koodilla.",
          "Vaihda PIN-koodi Asetuksista. Älä jaa sitä: jaettu PIN antaa kaikki oikeudet.",
        ],
      },
      {
        title: "Tapahtumat",
        items: [
          "+ Luo uusi tapahtuma: paikka, aika, panos ja tarvittaessa liigakausi. Tapahtuma saa oman PIN-koodin.",
          "Muokkaa korjaa ajan, paikan, panoksen tai kauden. Peruuta tapahtuma poistaa ottelut ja osallistujat ilman tilastomerkintöjä.",
          "Illan kulku on sama kuin järjestäjällä: kutsut, arvonta, tulokset, paikalla-merkinnät ja päättäminen.",
          "Maanantaiaamuna saat muistutuksen, jos tulevan viikon tapahtuman kutsut ovat lähettämättä.",
          "Tapahtumakortin Viestit-osiossa kirjoitat ylläpitona ja voit poistaa minkä tahansa viestin. Saat ilmoituksen, jos joku ilmoittaa viestin asiattomaksi.",
          "Jokaisella tapahtumalla on Viestit-asetus: vahvistetut pelaajat (oletus), kaikki osallistujat (myös PIN-kirjautuneet, merkitään vahvistamattomiksi) tai ei viestejä. Valitse se tapahtumaa luodessa tai Muokkaa-kohdasta.",
        ],
      },
      {
        title: "Pelaajat ja järjestäjät",
        items: [
          "Pelaajat-välilehdellä lisäät pelaajat ja heidän sähköpostinsa. Sähköposti tarvitaan PIN-vapaaseen kutsulinkkiin.",
          "Järjestäjän nimeäminen: Muokkaa → Nimeä järjestäjäksi → lähetä kutsulinkki. Oikeudet tulevat voimaan, kun hän avaa linkin.",
          "Roolin poisto astuu voimaan, kun järjestäjä avaa sovelluksen seuraavan kerran.",
        ],
      },
      {
        title: "Liiga ja asetukset",
        items: [
          "Asetuksissa luot kauden ja sen tapahtumat kerralla, määrität pelipaikat ja MobilePay-lippaan sekä julkaiset tiedotteet.",
          "Tiedote lähtee ilmoituksena kaikille klubin laitteille.",
        ],
      },
      {
        title: "DUPR",
        items: [
          "Avaa päättynyt tapahtuma ja paina Lähetä DUPR:iin.",
          "Vain DUPR-tilinsä yhdistäneiden pelaajien ottelut lähtevät. 0–0 ja tasapelit eivät lähde.",
        ],
      },
    ],
  },
  en: {
    player: [
      {
        title: "Signing in",
        items: [
          "Easiest: open the invitation email and tap the button in it. No club code or PIN needed.",
          "Otherwise: enter the club code, then the event PIN and your name. The organizer gives you the codes.",
          "The first time, your phone saves a passkey. After that you sign in with Face ID, fingerprint or your phone's passcode.",
          "You stay signed in until you tap Sign out.",
        ],
      },
      {
        title: "Events and signing up",
        items: [
          "The Events tab lists the club's upcoming events.",
          "📩 INVITED means you have been invited. Answer I'm in or Can't make it.",
          "For other events, tap Sign up. To cancel, tap You're in again.",
          "You can change your answer at any time before the event.",
          "If you haven't answered an invite, you get a reminder about a day and a half before the event.",
        ],
      },
      {
        title: "On the day",
        items: [
          "Open the event to see courts, rounds and Your matches.",
          "Results are entered by the organizer or admin. You get a notification when your result is in.",
          "Use the event's Messages section to tell others you're late or that a spot is open. Quick buttons cover the common messages.",
          "If writing requires verification, tap Send me a verification link. It goes to the email saved in your profile; open it on the phone you use Spinloop on.",
          "You can report an inappropriate message to the admin, or block its author on your own device. Messages are removed a week after the event.",
          "If the club uses a MobilePay box, the event has a pay button.",
        ],
      },
      {
        title: "League",
        items: [
          "The League tab appears when the club has a season running.",
          "Night points = placement points + attendance point. You get the attendance point for showing up, even if you didn't get to play.",
          "Players level on points share the same place and points.",
        ],
      },
      {
        title: "Profile and DUPR",
        items: [
          "Connect your DUPR account in your profile so club matches update your DUPR rating.",
          "Turn on notifications in your profile to get invites, reminders and results on your phone.",
          "The History tab shows your past matches.",
        ],
      },
    ],
    organizer: [
      {
        title: "Becoming an organizer",
        items: [
          "The admin makes you an organizer and sends an invite link to your email.",
          "Open the link once. Your phone saves a passkey, and the Organize tab appears.",
          "Signing in with an event PIN always gives player rights only, because the PIN is shared by everyone.",
        ],
      },
      {
        title: "Before the event",
        items: [
          "Open the event from the Organize tab.",
          "Add players from the club list or with + Add guest.",
          "Send invites: everyone gets an email and a phone notification. The email link signs them in without a PIN.",
          "The participant list shows who has confirmed, who is invited but hasn't answered, and who can't make it.",
        ],
      },
      {
        title: "During the event",
        items: [
          "Tap Present? for everyone who came. Players with a result are marked present automatically when the event ends.",
          "Generate matches with Americano, or set fixed pairs for a round robin.",
          "Americano also shares the two ends of the court evenly. If the ends are not equal (a wall close behind one baseline), name them under the venue in Settings — then the ends show up in the matches.",
          "Add round generates the next round. Add match adds a single match by hand.",
          "Enter results for each match. 0–0 means not played; a tie is a valid result.",
          "Post changes in the event's Messages section (e.g. court changed). Organizer messages always notify, and you can delete inappropriate messages.",
        ],
      },
      {
        title: "Ending the event",
        items: [
          "Tap End event. You're warned if a match has no result, or if someone who signed up didn't play and isn't marked present.",
          "Ending records placings, league points and player stats.",
          "To fix a result later: open the ended event, correct it and tap Recalculate placings.",
        ],
      },
      {
        title: "What organizers don't do",
        items: [
          "Creating, editing and deleting events, DUPR submission, seasons and club settings belong to the admin.",
        ],
      },
    ],
    admin: [
      {
        title: "Signing in",
        items: [
          "Sign in with the club code and the admin PIN.",
          "Change the PIN in Settings. Don't share it: the PIN gives full rights.",
        ],
      },
      {
        title: "Events",
        items: [
          "+ Create new event: venue, time, fee and optionally a league season. Each event gets its own PIN.",
          "Edit fixes the time, venue, fee or season. Cancel event deletes matches and participants without recording stats.",
          "Running the event works as for organizers: invites, match generation, results, attendance and ending.",
          "On Monday mornings you get a reminder if invites for the coming week's event haven't been sent.",
          "In the event card's Messages section you post as admin and can delete any message. You're notified if someone reports a message.",
          "Each event has a Messages setting: verified players (default), all participants (PIN sign-ins too, marked unverified) or no messages. Set it when creating the event or with Edit.",
        ],
      },
      {
        title: "Players and organizers",
        items: [
          "Add players and their emails on the Players tab. An email is needed for the PIN-free invite link.",
          "To appoint an organizer: Edit → Make organizer → send the invite link. Rights take effect when they open the link.",
          "Removing the role takes effect the next time the organizer opens the app.",
        ],
      },
      {
        title: "League and settings",
        items: [
          "In Settings you create a season and its events in one go, set up venues and the MobilePay box, and publish announcements.",
          "An announcement is sent as a notification to every device in the club.",
        ],
      },
      {
        title: "DUPR",
        items: [
          "Open an ended event and tap Submit to DUPR.",
          "Only matches of players who have connected their DUPR account are sent. 0–0 and ties are not sent.",
        ],
      },
    ],
  },
};

/**
 * Pickleballin perussäännöt omin sanoin.
 *
 * Teksti on kirjoitettu itse eikä kopioitu sääntökirjasta: USA
 * Pickleballin virallinen sääntökirja on tekijänoikeudella suojattu,
 * joten sitä ei voi liittää sovellukseen. Säännöt faktoina eivät ole
 * suojattuja, niiden sanamuoto on.
 *
 * Tässä on tarkoituksella vain se osa, joka on pysynyt samana vuodesta
 * toiseen, eikä yhtään sääntönumeroa: väärä numero on huonompi kuin ei
 * numeroa lainkaan. Sääntökirjaan tulee vuosittain kymmeniä muutoksia,
 * joten tarkka kohta haetaan virallisesta lähteestä.
 */
const RULES: Record<"fi" | "en", Section[]> = {
  fi: [
    {
      title: "Kenttä",
      items: [
        "Kenttä on 6,10 × 13,41 metriä — sama koko kaksin- ja nelinpelissä.",
        "Verkko on 91 cm korkea reunoilta ja 86 cm keskeltä.",
        "Verkon molemmin puolin on 2,13 metrin alue, jota kutsutaan keittiöksi (non-volley zone).",
      ],
    },
    {
      title: "Syöttö",
      items: [
        "Syöttö lyödään alakautta: kosketus tapahtuu vyötärön alapuolella ja maila liikkuu alhaalta ylöspäin.",
        "Myös pudotussyöttö on sallittu — pallo pudotetaan maahan ja lyödään pompun jälkeen.",
        "Syötetään vinottain vastakkaiseen ruutuun, molemmat jalat takarajan takana.",
        "Syöttöyrityksiä on yksi. Epäonnistunut syöttö on suoraan virhe, toista yritystä ei ole.",
        "Verkkoon osunut syöttö, joka menee oikeaan ruutuun, on pelissä. Uusintaa ei pelata.",
      ],
    },
    {
      title: "Kahden pompun sääntö",
      items: [
        "Syötön on annettava pompata ennen kuin palauttaja lyö sen.",
        "Palautuksen on annettava pompata ennen kuin syöttävä puoli lyö sen.",
        "Vasta näiden kahden pompun jälkeen pallon saa lyödä ilmasta.",
      ],
    },
    {
      title: "Keittiö",
      items: [
        "Keittiössä ei saa lyödä palloa ilmasta. Se on virhe, vaikka vain jalka koskisi viivaa.",
        "Myös lyönnin jälkeinen vauhti lasketaan: jos liike vie keittiöön, lyönti on virhe.",
        "Keittiössä saa seistä ja pelata vapaasti, kunhan pallo on ensin pompannut.",
      ],
    },
    {
      title: "Pisteet",
      items: [
        "Perinteisessä laskutavassa pisteen saa vain syöttävä joukkue. Vastustajan virhe palauttaa syötön, ei pistettä.",
        "Kilpailussa pelataan yleensä 11 pisteeseen, ja voittoon tarvitaan kahden pisteen ero.",
        "Nelinpelissä molemmat pelaajat syöttävät ennen kuin syöttövuoro vaihtuu — paitsi pelin ensimmäisellä syöttövuorolla, jolloin syöttää vain toinen.",
        "Pisteet sanotaan nelinpelissä kolmena numerona: syöttävän joukkueen pisteet, vastustajan pisteet ja syöttäjän numero.",
      ],
    },
    {
      title: "Virheet",
      items: [
        "Pallo jää verkkoon tai menee rajojen ulkopuolelle.",
        "Pallo pomppaa kahdesti ennen lyöntiä.",
        "Lyönti ilmasta keittiössä tai sen viivalla.",
        "Pelaaja, maila tai vaatetus koskee verkkoa pallon ollessa pelissä.",
      ],
    },
    {
      title: "Viivat",
      items: [
        "Viivaan osunut pallo on sisällä.",
        "Yksi poikkeus: syöttö, joka osuu keittiön viivaan, on ulkona.",
      ],
    },
    {
      title: "Tämä on tiivistelmä",
      items: [
        "Yllä on vain perusteet. Virallinen sääntökirja on laajempi ja muuttuu vuosittain — ristiriitatilanteessa viralliset säännöt pätevät.",
        "Pistemäärä sovitaan ennen tapahtuman alkua: 11 on kilpailun vakio, 15 tavallinen vaihtoehto. Turnauksissa näkee lisäksi 21:tä ja rallikohtaista laskentaa.",
        "Tämän illan pistetavoite näkyy tapahtuman tiedoissa, ja se ratkaisee miten juuri nyt pelataan.",
      ],
    },
  ],
  en: [
    {
      title: "The court",
      items: [
        "The court is 6.10 × 13.41 m — the same size for singles and doubles.",
        "The net is 91 cm high at the posts and 86 cm at the centre.",
        "A 2.13 m area on each side of the net is the non-volley zone, known as the kitchen.",
      ],
    },
    {
      title: "The serve",
      items: [
        "The serve is hit underhand: contact below the waist, with the paddle moving upward.",
        "The drop serve is also allowed — drop the ball and hit it after the bounce.",
        "Serve diagonally into the opposite court, both feet behind the baseline.",
        "You get one attempt. A missed serve is a fault; there is no second serve.",
        "A serve that clips the net and lands in the correct court is live. It is not replayed.",
      ],
    },
    {
      title: "The two-bounce rule",
      items: [
        "The serve must bounce before the returner hits it.",
        "The return must bounce before the serving side hits it.",
        "Only after those two bounces may the ball be volleyed.",
      ],
    },
    {
      title: "The kitchen",
      items: [
        "You may not volley while in the kitchen. It is a fault even if only a foot touches the line.",
        "Momentum counts too: if the follow-through carries you into the kitchen, the volley is a fault.",
        "You may stand and play in the kitchen freely, as long as the ball has bounced first.",
      ],
    },
    {
      title: "Scoring",
      items: [
        "In traditional scoring only the serving team scores. An opponent's fault returns the serve, not a point.",
        "In competition games usually go to 11 points, and you must win by two.",
        "In doubles both partners serve before the serve changes sides — except on the first service turn of the game, when only one player serves.",
        "In doubles the score is called as three numbers: serving team's score, opponents' score, and server number.",
      ],
    },
    {
      title: "Faults",
      items: [
        "The ball goes into the net or out of bounds.",
        "The ball bounces twice before it is hit.",
        "A volley played in the kitchen or on its line.",
        "A player, paddle or clothing touches the net while the ball is live.",
      ],
    },
    {
      title: "Lines",
      items: [
        "A ball touching a line is in.",
        "One exception: a serve that touches the kitchen line is out.",
      ],
    },
    {
      title: "This is a summary",
      items: [
        "The above covers the basics only. The official rulebook is longer and changes every year — where they conflict, the official rules apply.",
        "The target is agreed before the event starts: 11 is the competition standard, 15 a common alternative. Tournaments additionally use 21 and rally scoring.",
        "Tonight's points target is shown in the event details, and that is what decides how you play right now.",
      ],
    },
  ],
};

const TAB_LABEL: Record<"fi" | "en", Record<GuideTab, string>> = {
  fi: { player: "Pelaaja", organizer: "Järjestäjä", admin: "Ylläpitäjä", rules: "Säännöt" },
  en: { player: "Player", organizer: "Organizer", admin: "Admin", rules: "Rules" },
};

export function guideRoleFor(role?: string): GuideRole {
  if (role === "club_admin") return "admin";
  if (role === "organizer") return "organizer";
  return "player";
}

export default function HelpGuide({ role, lang, onClose }: {
  role?: string;
  lang: "fi" | "en";
  onClose: () => void;
}) {
  const [tab, setTab] = useState<GuideTab>(guideRoleFor(role));

  // Androidin takaisin-nappi sulkee ohjeen eikä koko näkymää.
  useEffect(() => {
    window.history.pushState({ help: true }, "");
    const onPop = () => onClose();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [onClose]);

  const close = () => {
    if (window.history.state?.help) window.history.back();
    else onClose();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999, background: "var(--sl-bg)",
      overflowY: "auto", fontFamily: "'Inter', sans-serif",
      paddingTop: "env(safe-area-inset-top, 0px)",
      paddingBottom: "calc(24px + var(--sl-safe-bottom))",
    }}>
      <div style={{ maxWidth: 520, margin: "0 auto", padding: "16px 16px 0" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "var(--sl-text)" }}>
            {lang === "fi" ? "Ohjeet" : "Help"}
          </div>
          <button onClick={close} aria-label={lang === "fi" ? "Sulje" : "Close"} style={{
            border: "none", background: "var(--sl-soft)", color: "var(--sl-text)",
            borderRadius: 10, padding: "8px 14px", fontSize: 14, fontWeight: 700, cursor: "pointer",
          }}>
            ✕
          </button>
        </div>

        <div role="tablist" style={{ display: "flex", gap: 6, marginBottom: 16 }}>
          {(["player", "organizer", "admin", "rules"] as GuideTab[]).map(r => (
            <button key={r} role="tab" aria-selected={tab === r} onClick={() => setTab(r)} style={{
              flex: 1, padding: "9px 0", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer",
              border: `1px solid ${tab === r ? "var(--sl-primary)" : "var(--sl-border)"}`,
              background: tab === r ? "var(--sl-primary)" : "transparent",
              color: tab === r ? "#fff" : "var(--sl-text)",
            }}>
              {TAB_LABEL[lang][r]}
            </button>
          ))}
        </div>

        {(tab === "rules" ? RULES[lang] : GUIDE[lang][tab]).map(sec => (
          <div key={sec.title} style={{
            background: "var(--sl-card, #fff)", border: "1px solid var(--sl-border)",
            borderRadius: 14, padding: "14px 16px", marginBottom: 12,
          }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "var(--sl-text)", marginBottom: 8 }}>
              {sec.title}
            </div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {sec.items.map(it => (
                <li key={it} style={{ fontSize: 13, lineHeight: 1.55, color: "var(--sl-text)", marginBottom: 6 }}>
                  {it}
                </li>
              ))}
            </ul>
          </div>
        ))}

        {tab === "rules" && (
          <div style={{ fontSize: 12, color: "var(--sl-muted)", lineHeight: 1.55, margin: "4px 2px 8px" }}>
            {lang === "fi"
              ? "Viralliset säännöt julkaisee USA Pickleball. Tiivistelmä on kirjoitettu omin sanoin, eikä se korvaa sääntökirjaa: "
              : "The official rules are published by USA Pickleball. This summary is written in our own words and does not replace the rulebook: "}
            <a href="https://usapickleball.org/what-is-pickleball/official-rules/"
               target="_blank" rel="noopener noreferrer"
               style={{ color: "var(--sl-primary)" }}>
              usapickleball.org
            </a>
          </div>
        )}

        <div style={{ fontSize: 12, color: "var(--sl-muted)", textAlign: "center", margin: "8px 0 4px" }}>
          {lang === "fi" ? "Kysyttävää? info@spinloop.app" : "Questions? info@spinloop.app"}
        </div>
      </div>
    </div>
  );
}
